import React from 'react';
import Logo from '../common/Logo';

/* ──────────────────────────────────────────────
   Visual preview components for each feature
   ────────────────────────────────────────────── */

const TasksPreview = () => {
  const tasks = [
    { title: 'Design review', cat: 'Work', color: '#9333EA', status: 'In Progress', statusColor: '#007AFF', deadline: 'Tomorrow', assignee: '→ Sarah', overdue: false },
    { title: 'Grocery shopping', cat: 'Personal', color: '#10B981', status: null, statusColor: '', deadline: null, assignee: null, overdue: false },
    { title: 'Sprint planning', cat: 'Work', color: '#3B82F6', status: 'In Progress', statusColor: '#007AFF', deadline: 'Mar 5', assignee: null, overdue: false },
    { title: 'Dentist appointment', cat: 'Health', color: '#F59E0B', status: null, statusColor: '', deadline: '2d overdue', assignee: null, overdue: true },
    { title: 'Update API docs', cat: 'Work', color: '#9333EA', status: 'Completed', statusColor: '#34C759', deadline: null, assignee: '→ Mike', overdue: false },
  ];
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-orange-500/60" />
        <span className="text-[11px] font-medium text-neutral-400">Tasks</span>
        <div className="ml-auto flex gap-1.5">
          <span className="text-[9px] px-2.5 py-0.5 rounded-full bg-neutral-800 text-neutral-400">All</span>
          <span className="text-[9px] px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-medium">Active</span>
        </div>
      </div>
      <div className="space-y-1.5 p-2.5">
        {tasks.map((t, i) => (
          <div key={i} className="flex items-center rounded-xl border border-neutral-800 overflow-hidden bg-neutral-900">
            {/* Left color bar */}
            <div className="w-1 self-stretch" style={{ backgroundColor: t.color }} />
            <div className="flex-1 px-3 py-2">
              <p className={`text-[11px] font-medium ${t.status === 'Completed' ? 'text-neutral-500 line-through' : 'text-neutral-200'}`}>{t.title}</p>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                {t.status && (
                  <span className="flex items-center gap-1 text-[8px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: t.statusColor + '20', color: t.statusColor }}>
                    <span className="w-1 h-1 rounded-full" style={{ backgroundColor: t.statusColor }} />
                    {t.status}
                  </span>
                )}
                {t.assignee && <span className="text-[9px] text-purple-400">{t.assignee}</span>}
                {t.deadline && (
                  <span className={`text-[9px] ${t.overdue ? 'text-red-400 font-medium' : 'text-neutral-500'}`}>{t.deadline}</span>
                )}
                <span className="text-[9px] text-neutral-600">{t.cat}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GanttPreview = () => {
  const tasks = [
    { label: 'Design system', color: '#3B82F6', x: 32, w: 152, row: 0 },
    { label: 'API integration', color: '#10B981', x: 112, w: 128, row: 1 },
    { label: 'User testing', color: '#9333EA', x: 208, w: 112, row: 2 },
    { label: 'Launch prep', color: '#F59E0B', x: 272, w: 88, row: 3 },
    { label: 'QA Testing', color: '#EC4899', x: 160, w: 100, row: 4 },
  ];
  const BAR_H = 20;
  const ROW_H = 34;
  const PAD_TOP = 8;
  const barY = (row: number) => PAD_TOP + row * ROW_H;
  const VW = 400;
  const VH = 194;

  // Dependency arrow from Design system → User testing
  const x1 = tasks[0].x + tasks[0].w;
  const y1 = barY(0) + BAR_H / 2;
  const x2 = tasks[2].x;
  const y2 = barY(2) + BAR_H / 2;
  const cpOffset = Math.max(50, Math.abs(x2 - x1) * 0.7);
  const curvePath = `M${x1},${y1} C${x1 + cpOffset},${y1} ${x2 - cpOffset},${y2} ${x2},${y2}`;

  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-cyan-500/60" />
        <span className="text-[11px] font-medium text-neutral-400">Gantt Chart</span>
      </div>
      <div className="px-2 py-2">
        <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ display: 'block' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <line key={i} x1={i * 75} y1={0} x2={i * 75} y2={VH} stroke="#262626" strokeWidth={0.5} />
          ))}
          {tasks.map((task, i) => (
            <g key={i}>
              <rect x={task.x} y={barY(task.row)} width={task.w} height={BAR_H} rx={4} fill={task.color} style={{ filter: `drop-shadow(0 0 4px ${task.color}40)` }} />
              <text x={task.x + 8} y={barY(task.row) + BAR_H / 2 + 3.5} fill="white" fontSize={8} fontWeight={500} fontFamily="system-ui, sans-serif">{task.label}</text>
            </g>
          ))}
          <path d={curvePath} fill="none" stroke="#A78BFA" strokeWidth={2} opacity={0.5} />
          <polygon points={`${x2},${y2} ${x2 - 6},${y2 - 3} ${x2 - 6},${y2 + 3}`} fill="#A78BFA" opacity={0.5} />
        </svg>
      </div>
    </div>
  );
};

const SenseiPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden flex flex-col">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-purple-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Sensei AI</span>
    </div>
    <div className="px-3 py-3 flex flex-col gap-2.5">
      {/* User message — right-aligned, primary color background */}
      <div className="flex justify-end">
        <div className="px-3.5 py-2 rounded-2xl text-[11px] font-medium max-w-[80%] bg-[#007AFF] text-white">
          What tasks are overdue?
        </div>
      </div>
      {/* AI response — left-aligned with avatar */}
      <div className="flex items-start gap-2">
        <div className="w-5 h-5 rounded-full bg-[#007AFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-2.5 h-2.5 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div className="px-3.5 py-2 rounded-2xl bg-neutral-800 text-neutral-200 text-[11px] leading-relaxed">
          <p>You have 2 overdue tasks:</p>
          <p className="mt-0.5 text-neutral-400">- Design review (due yesterday)</p>
          <p className="mt-0.5 text-neutral-400">- API docs (due 3 days ago)</p>
        </div>
      </div>
      {/* User follow-up */}
      <div className="flex justify-end">
        <div className="px-3.5 py-2 rounded-2xl text-[11px] font-medium max-w-[85%] bg-[#007AFF] text-white">
          Create task "QA Testing" and assign to Sarah
        </div>
      </div>
      {/* AI response with action card */}
      <div className="flex items-start gap-2">
        <div className="w-5 h-5 rounded-full bg-[#007AFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-2.5 h-2.5 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="px-3.5 py-2 rounded-2xl bg-neutral-800 text-neutral-200 text-[11px] leading-relaxed">
            Sure! I'll create that task for you.
          </div>
          {/* Action card — matches iOS borderRadius 12, border, padding 12 */}
          <div className="rounded-xl border border-neutral-700 bg-neutral-800/80 p-2.5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 rounded bg-[#007AFF]/20 flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p className="text-[10px] text-neutral-300 font-medium flex-1">Create Task: QA Testing → Sarah</p>
            </div>
            {/* Approved badge — matches iOS success color + opacity bg */}
            <div className="flex items-center gap-1 ml-6 bg-green-500/10 w-fit px-2 py-0.5 rounded-lg">
              <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-[9px] text-green-400 font-semibold">Approved</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    {/* Input bar — matches iOS pill with border and send button */}
    <div className="px-3 pb-2.5 pt-1">
      <div className="flex items-center gap-2 rounded-full px-3.5 py-1.5 border border-neutral-700">
        <span className="text-[10px] text-neutral-500 flex-1">Ask Sensei anything...</span>
        <div className="w-5 h-5 rounded-full bg-[#007AFF] flex items-center justify-center">
          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </div>
      </div>
    </div>
  </div>
);

const CalendarPreview = () => {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  // 5 weeks of dates for a month view (simulating March)
  const cells = [
    [null, null, null, null, null, 1, 2],
    [3, 4, 5, 6, 7, 8, 9],
    [10, 11, 12, 13, 14, 15, 16],
    [17, 18, 19, 20, 21, 22, 23],
    [24, 25, 26, 27, 28, 29, 30],
  ];
  const taskDots: Record<number, string[]> = {
    4: ['#3B82F6'],
    7: ['#3B82F6', '#10B981'],
    11: ['#F59E0B'],
    13: ['#9333EA', '#3B82F6'],
    18: ['#EC4899'],
    21: ['#10B981', '#F59E0B'],
    25: ['#3B82F6'],
    28: ['#9333EA'],
  };
  const today = 13;
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-blue-500/60" />
        <span className="text-[11px] font-medium text-neutral-400">Calendar</span>
        <span className="ml-auto text-[10px] text-neutral-500">March 2026</span>
      </div>
      <div className="p-3">
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {days.map((d, i) => (
            <div key={i} className="text-center text-[9px] text-neutral-500 font-medium py-1">{d}</div>
          ))}
        </div>
        {cells.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-0.5">
            {week.map((day, di) => (
              <div key={di} className="flex flex-col items-center py-1">
                {day ? (
                  <>
                    <span className={`text-[10px] w-5 h-5 flex items-center justify-center rounded-full ${day === today ? 'bg-purple-500 text-white font-bold' : 'text-neutral-300'}`}>
                      {day}
                    </span>
                    {taskDots[day] && (
                      <div className="flex gap-0.5 mt-0.5">
                        {taskDots[day].map((c, ci) => (
                          <div key={ci} className="w-1 h-1 rounded-full" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    )}
                  </>
                ) : <span className="w-5 h-5" />}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const DashboardPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Dashboard</span>
      <span className="ml-auto text-[10px] text-neutral-500">Welcome back, Chris 👋</span>
    </div>
    <div className="p-3 space-y-2.5">
      {/* Stat cards row — matches web 4-column grid */}
      <div className="grid grid-cols-4 gap-1.5">
        {[
          { label: 'Active Tasks', value: '12', color: '#fff' },
          { label: 'In Progress', value: '5', color: '#007AFF' },
          { label: 'Overdue', value: '3', color: '#FF3B30' },
          { label: 'Completion', value: '75%', color: '#34C759' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl border border-neutral-800 bg-neutral-900 p-2.5">
            <p className="text-[8px] text-neutral-500">{s.label}</p>
            <p className="text-[14px] font-bold mt-0.5" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>
      {/* Overdue section */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-2.5">
        <div className="flex items-center gap-1.5 mb-2">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
          <p className="text-[10px] text-neutral-200 font-semibold">Overdue (3)</p>
        </div>
        {[
          { title: 'Design review', cat: 'Work', color: '#9333EA', deadline: '2d overdue' },
          { title: 'API documentation', cat: 'Dev', color: '#3B82F6', deadline: '1d overdue' },
        ].map((t, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 rounded-lg hover:bg-neutral-800/50 px-1.5">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
            <p className="text-[10px] text-neutral-200 font-medium flex-1 truncate">{t.title}</p>
            <span className="text-[8px] text-neutral-600">{t.cat}</span>
            <span className="text-[9px] text-red-400 font-medium">{t.deadline}</span>
          </div>
        ))}
      </div>
      {/* Today's tasks */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-2.5">
        <p className="text-[10px] text-neutral-200 font-semibold mb-2">Today (5)</p>
        {[
          { title: 'Sprint planning', cat: 'Work', color: '#3B82F6', status: 'In Progress', statusColor: '#007AFF' },
          { title: 'Dentist at 3pm', cat: 'Health', color: '#F59E0B', status: null, statusColor: '' },
          { title: 'Review PR #42', cat: 'Dev', color: '#10B981', status: 'In Progress', statusColor: '#007AFF' },
        ].map((t, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 rounded-lg hover:bg-neutral-800/50 px-1.5">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
            <p className="text-[10px] text-neutral-200 font-medium flex-1 truncate">{t.title}</p>
            {t.status && (
              <span className="text-[7px] px-1 py-0.5 rounded-full" style={{ backgroundColor: t.statusColor + '20', color: t.statusColor }}>{t.status}</span>
            )}
            <span className="text-[8px] text-neutral-600">{t.cat}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ConnectionsPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-blue-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Connections</span>
      <div className="ml-auto flex items-center gap-2">
        <span className="text-[9px] px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400">Connections</span>
        <span className="text-[9px] px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400 relative">
          Requests
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />
        </span>
      </div>
    </div>
    <div className="p-2.5 space-y-1.5">
      {[
        { name: 'Sarah Chen', email: 'sarah@email.com', initials: 'SC', color: '#EC4899' },
        { name: 'Mike Johnson', email: 'mike@email.com', initials: 'MJ', color: '#3B82F6' },
        { name: 'Emma Wilson', email: 'emma@email.com', initials: 'EW', color: '#10B981' },
      ].map((c, i) => (
        <div key={i} className="flex items-center gap-2.5 rounded-xl border border-neutral-800 bg-neutral-900 p-2.5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white" style={{ backgroundColor: c.color }}>
            {c.initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-neutral-200 font-semibold">{c.name}</p>
            <p className="text-[9px] text-neutral-500">{c.email}</p>
          </div>
          <span className="text-[8px] text-red-400 px-1.5 py-0.5 rounded-lg bg-red-500/10 font-medium">Remove</span>
        </div>
      ))}
    </div>
  </div>
);

const HabitsPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Daily Habits</span>
    </div>
    <div className="p-3 space-y-2">
      {[
        { title: 'Morning run', streak: 14, best: 21, icon: '🏃', color: '#FF3B30', done: true, reminder: '6:30 AM' },
        { title: 'Read 30 mins', streak: 7, best: 7, icon: '📚', color: '#3B82F6', done: true, reminder: '8:00 PM' },
        { title: 'Meditate', streak: 3, best: 12, icon: '🧘', color: '#9333EA', done: false, reminder: '7:00 AM' },
        { title: 'Drink 2L water', streak: 0, best: 5, icon: '💧', color: '#0EA5E9', done: false, reminder: null },
      ].map((h, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl bg-neutral-800/60 p-3">
          {/* Square icon container — 48x48, borderRadius 12 */}
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: h.color }}>
            <span className="text-sm">{h.icon}</span>
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className={`text-[11px] font-semibold ${h.done ? 'text-neutral-500 line-through opacity-60' : 'text-neutral-200'}`}>{h.title}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[9px] text-neutral-500">🔥 {h.streak} days</span>
              <span className="text-[9px] text-neutral-600">•</span>
              <span className="text-[9px] text-neutral-600">Best: {h.best}</span>
            </div>
            {h.reminder && (
              <p className="text-[8px] text-neutral-600 mt-0.5">🔔 Daily at {h.reminder}</p>
            )}
          </div>
          {/* Checkbox circle */}
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${h.done ? 'bg-[#007AFF] border-[#007AFF]' : 'border-neutral-600'}`}>
            {h.done && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ListsPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-pink-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Lists</span>
    </div>
    <div className="p-3">
      {/* Stats bar */}
      <div className="flex gap-2 mb-3">
        {[
          { label: 'Total Lists', value: '6' },
          { label: 'Total Items', value: '23' },
          { label: 'Shared', value: '3' },
        ].map((s, i) => (
          <div key={i} className="flex-1 rounded-lg bg-neutral-800/60 p-2 text-center">
            <p className="text-[12px] font-bold text-neutral-200">{s.value}</p>
            <p className="text-[7px] text-neutral-500">{s.label}</p>
          </div>
        ))}
      </div>
      {/* 2-column gradient card grid — matches iOS LinearGradient cards */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { name: 'Groceries', items: 8, done: 3, icon: '🛒', gradient: 'from-[#FF6B6B] to-[#ee5a24]', shared: true, members: 2 },
          { name: 'Work tasks', items: 5, done: 1, icon: '💼', gradient: 'from-[#667eea] to-[#764ba2]', shared: false, members: 1 },
          { name: 'Holiday plan', items: 6, done: 4, icon: '✈️', gradient: 'from-[#4facfe] to-[#00f2fe]', shared: true, members: 3 },
          { name: 'Books to read', items: 4, done: 0, icon: '📚', gradient: 'from-[#43e97b] to-[#38f9d7]', shared: false, members: 1 },
        ].map((l, i) => (
          <div key={i} className={`rounded-xl bg-gradient-to-br ${l.gradient} p-3 flex flex-col`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-base">{l.icon}</span>
              {l.shared && (
                <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              )}
            </div>
            <p className="text-[11px] font-bold text-white">{l.name}</p>
            <p className="text-[9px] text-white/80 mb-1.5">{l.items} items</p>
            <div className="h-1 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white/80 rounded-full" style={{ width: `${(l.done / l.items) * 100}%` }} />
            </div>
            <p className="text-[7px] text-white/70 mt-1">{l.members > 1 ? `${l.members} members` : 'Just you'}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MoodPreview = () => {
  const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  // Simplified last-2-weeks calendar with mood data
  const moodDays: Record<number, { emoji: string; color: string }> = {
    17: { emoji: '😊', color: '#4ade80' },
    18: { emoji: '😐', color: '#facc15' },
    19: { emoji: '😊', color: '#4ade80' },
    20: { emoji: '😔', color: '#f87171' },
    21: { emoji: '😊', color: '#4ade80' },
    22: { emoji: '🤩', color: '#4ade80' },
    24: { emoji: '😊', color: '#4ade80' },
    25: { emoji: '😌', color: '#4ade80' },
    26: { emoji: '😐', color: '#facc15' },
    27: { emoji: '🙂', color: '#4ade80' },
  };
  const weeks = [
    [16, 17, 18, 19, 20, 21, 22],
    [23, 24, 25, 26, 27, 28, null],
  ];
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-orange-500/60" />
        <span className="text-[11px] font-medium text-neutral-400">Feelings</span>
        <div className="ml-auto flex gap-1">
          {['Day', 'Week', 'Month'].map((p, i) => (
            <span key={p} className={`text-[8px] px-1.5 py-0.5 rounded-md font-medium ${i === 2 ? 'bg-[#007AFF] text-white' : 'bg-neutral-800 text-neutral-500'}`}>{p}</span>
          ))}
        </div>
      </div>
      <div className="p-3">
        {/* Stats */}
        <div className="flex gap-2 mb-3">
          <div className="flex-1 rounded-lg bg-neutral-800/60 p-2 text-center">
            <p className="text-[12px] font-bold text-neutral-200">10</p>
            <p className="text-[7px] text-neutral-500">Total entries</p>
          </div>
          <div className="flex-1 rounded-lg bg-neutral-800/60 p-2 text-center">
            <p className="text-[12px] font-bold text-neutral-200">3.8</p>
            <p className="text-[7px] text-neutral-500">Avg intensity</p>
          </div>
        </div>
        {/* Calendar grid with mood backgrounds */}
        <div className="rounded-lg bg-neutral-800/40 p-2">
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {dayHeaders.map((d, i) => (
              <div key={i} className="text-center text-[7px] text-neutral-600 font-medium">{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-0.5">
              {week.map((day, di) => {
                const mood = day ? moodDays[day] : null;
                const isToday = day === 27;
                return (
                  <div
                    key={di}
                    className={`flex flex-col items-center py-1 rounded ${mood ? '' : ''} ${isToday ? 'ring-1 ring-[#007AFF]' : ''}`}
                    style={mood ? { backgroundColor: mood.color + '30' } : undefined}
                  >
                    {day ? (
                      <>
                        <span className="text-[8px] text-neutral-400">{day}</span>
                        {mood && <span className="text-[10px] mt-0.5">{mood.emoji}</span>}
                      </>
                    ) : <span className="text-[8px]">&nbsp;</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {/* Legend */}
        <div className="flex justify-center gap-3 mt-2">
          {[
            { label: 'Positive', color: '#4ade80' },
            { label: 'Mixed', color: '#facc15' },
            { label: 'Negative', color: '#f87171' },
          ].map((l, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: l.color }} />
              <span className="text-[7px] text-neutral-600">{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AffirmationsPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-pink-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Affirmations</span>
      <span className="ml-auto text-[9px] text-neutral-600">12 positive thoughts</span>
    </div>
    <div className="p-3">
      {/* Daily / All toggle — matches iOS segmented control */}
      <div className="flex gap-0.5 bg-neutral-800/60 rounded-lg p-0.5 mb-3">
        <div className="flex-1 text-center py-1 rounded-md bg-[#007AFF] text-[9px] text-white font-semibold">Daily</div>
        <div className="flex-1 text-center py-1 rounded-md text-[9px] text-neutral-500 font-medium">All</div>
      </div>
      {/* Daily card — matches iOS sunny icon + large centered text */}
      <div className="rounded-xl bg-neutral-800/60 p-5 text-center">
        <div className="w-7 h-7 rounded-full bg-[#007AFF]/20 flex items-center justify-center mx-auto mb-2">
          <span className="text-sm">☀️</span>
        </div>
        <p className="text-[10px] text-neutral-400 font-semibold mb-2">Today's Affirmation</p>
        <p className="text-[14px] text-neutral-200 leading-relaxed font-medium" style={{ lineHeight: '1.6' }}>
          I am capable of achieving great things, one step at a time.
        </p>
        <div className="flex items-center justify-center gap-1 mt-3 rounded-lg bg-[#007AFF]/10 py-1.5 px-3 mx-auto w-fit">
          <svg className="w-2.5 h-2.5 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="text-[9px] text-[#007AFF] font-medium">Next Affirmation</span>
        </div>
      </div>
    </div>
  </div>
);

const NotesPreview = () => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full bg-indigo-500/60" />
      <span className="text-[11px] font-medium text-neutral-400">Personal Notes</span>
    </div>
    <div className="p-3 space-y-2">
      {[
        { title: 'Meeting notes', preview: 'Discussed Q2 roadmap, key decisions on the new feature rollout and team allocation...', date: 'Updated 2h ago', star: true, reminder: 'Mar 3, 9:00 AM' },
        { title: 'Recipe ideas', preview: 'Thai green curry, homemade pasta, BBQ marinade for weekend...', date: 'Updated yesterday', star: false, reminder: null },
        { title: 'Book list', preview: 'Atomic Habits, Deep Work, The Lean Startup, Thinking Fast...', date: 'Updated 3 days ago', star: true, reminder: null },
        { title: 'Travel plans', preview: 'Flights booked for June, need to sort accommodation and...', date: 'Updated last week', star: false, reminder: 'Jun 1, 10:00 AM' },
      ].map((n, i) => (
        <div key={i} className="rounded-xl bg-neutral-800/60 p-3">
          {/* Header: title + star + delete */}
          <div className="flex items-center gap-1.5 mb-1">
            <p className="text-[11px] text-neutral-200 font-bold flex-1 truncate">{n.title}</p>
            <span className={`text-[12px] ${n.star ? 'text-yellow-400' : 'text-neutral-700'}`}>★</span>
          </div>
          {/* Preview text */}
          <p className="text-[9px] text-neutral-500 leading-relaxed line-clamp-2 mb-1.5">{n.preview}</p>
          {/* Footer: date + reminder */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] text-neutral-600">{n.date}</span>
            {n.reminder && (
              <span className="text-[8px] text-[#007AFF] flex items-center gap-0.5">
                🔔 {n.reminder}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ──────────────────────────────────────────────
   Feature data
   ────────────────────────────────────────────── */

interface Feature {
  title: string;
  tagline: string;
  description: string;
  useCase: string;
  preview: React.ReactNode;
  appOnly?: boolean;
}

const features: Feature[] = [
  {
    title: 'Tasks',
    tagline: 'Manage everything in one place',
    description: 'Create tasks with categories, priorities, deadlines, and reminders. Assign tasks to your connections and track progress with status updates.',
    useCase: 'Plan your entire week by breaking big goals into manageable tasks — assign some to your partner, set deadlines for work items, and never miss a thing.',
    preview: <TasksPreview />,
  },
  {
    title: 'Gantt Chart',
    tagline: 'Visualise your timeline',
    description: 'See all your tasks laid out on a drag-and-drop timeline. Add dependency arrows between tasks, sort by start date or deadline, and zoom in for detail.',
    useCase: 'Planning a house move? Map out every step from viewings to completion day, link dependent tasks together, and see the full picture at a glance.',
    preview: <GanttPreview />,
  },
  {
    title: 'Sensei AI',
    tagline: 'Your AI-powered assistant',
    description: 'Chat with Sensei to create tasks, get summaries of overdue items, and manage your day — all through natural conversation. Every action needs your approval first.',
    useCase: 'On a busy morning, just tell Sensei "What\'s overdue?" or "Create a task for the dentist on Friday" — it handles the admin so you can focus on doing.',
    preview: <SenseiPreview />,
  },
  {
    title: 'Calendar',
    tagline: 'Your schedule at a glance',
    description: 'View all your tasks and deadlines on a monthly calendar. Colour-coded dots show you busy days, and tapping a date reveals everything due.',
    useCase: 'Quickly spot a packed Thursday when you\'re trying to schedule a new meeting — see which days have breathing room and plan accordingly.',
    preview: <CalendarPreview />,
  },
  {
    title: 'Dashboard',
    tagline: 'Track your progress',
    description: 'See your active, completed, and overdue tasks at a glance. A weekly activity chart shows your productivity trends over time.',
    useCase: 'Start each Monday by reviewing your dashboard — celebrate last week\'s wins and identify what still needs attention.',
    preview: <DashboardPreview />,
  },
  {
    title: 'Connections',
    tagline: 'Collaborate with your people',
    description: 'Connect with friends, family, or colleagues. Assign tasks to each other, share lists, and get notified when things get done.',
    useCase: 'Share household chores with your flatmates, assign wedding planning tasks to your partner, or delegate work items to your team.',
    preview: <ConnectionsPreview />,
  },
  {
    title: 'Habits',
    tagline: 'Build streaks that stick',
    description: 'Track daily habits with streak counters and visual progress bars. Set reminders so you never break the chain.',
    useCase: 'Trying to read every day? Set a "Read 30 mins" habit, get a reminder at 8pm, and watch your streak grow week after week.',
    preview: <HabitsPreview />,
  },
  {
    title: 'Shared Lists',
    tagline: 'Lists that everyone can edit',
    description: 'Create lists and share them with your connections. Everyone can add, check off, and rearrange items in real time.',
    useCase: 'Create a shared grocery list with your partner — they add items during the day, you tick them off as you shop. Simple.',
    preview: <ListsPreview />,
  },
  {
    title: 'Mood Tracker',
    tagline: 'Understand how you feel',
    description: 'Log your mood each day with a simple tap. See weekly trends to spot patterns and understand what affects your wellbeing.',
    useCase: 'Notice you always feel low on Sundays? Spot the pattern and build a routine that helps — like a morning walk or calling a friend.',
    preview: <MoodPreview />,
  },
  {
    title: 'Affirmations',
    tagline: 'Start the day with positivity',
    description: 'Get daily affirmations to keep you motivated. Swipe through a curated collection of positive thoughts designed to boost your mindset.',
    useCase: 'Open the app each morning and read your daily affirmation — a simple 10-second habit that sets a positive tone for the day ahead.',
    preview: <AffirmationsPreview />,
  },
  {
    title: 'Personal Notes',
    tagline: 'Your private journal',
    description: 'Jot down thoughts, meeting notes, recipes, or anything else. Set reminders on notes so important ones resurface when you need them.',
    useCase: 'After a meeting, quickly capture key decisions and action items. Set a reminder for next week so you follow up at the right time.',
    preview: <NotesPreview />,
  },
  {
    title: 'Budget',
    tagline: 'Stay on top of your finances',
    description: 'Simple spending tracker with categories, monthly totals, and visual breakdowns to help you stay on top of your finances.',
    useCase: 'Log your daily purchases, set monthly budgets by category, and see exactly where your money goes — no spreadsheets needed.',
    preview: <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 p-8 flex flex-col items-center justify-center"><span className="text-4xl mb-2">💰</span><span className="text-[11px] text-neutral-400 font-medium">Budget</span></div>,
  },
  {
    title: 'Analytics',
    tagline: 'Insights to help you improve',
    description: 'Productivity insights with 7-day trends, habit consistency scores, mood patterns, and AI-powered summaries to help you improve.',
    useCase: 'Review your weekly analytics every Sunday — see which habits stuck, how your mood shifted, and get AI suggestions for the week ahead.',
    preview: <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 p-8 flex flex-col items-center justify-center"><span className="text-4xl mb-2">📊</span><span className="text-[11px] text-neutral-400 font-medium">Analytics</span></div>,
  },
  {
    title: 'Developer Support',
    tagline: 'Real human support, not a bot',
    description: 'Chat directly with the taskDojo founder from within the app. Get help, share feedback, or request features — real human support, not a bot.',
    useCase: 'Found a bug or have a feature idea? Open the support chat and talk directly to the developer — no tickets, no waiting, just a real conversation.',
    preview: <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 p-8 flex flex-col items-center justify-center"><span className="text-4xl mb-2">💬</span><span className="text-[11px] text-neutral-400 font-medium">Developer Support</span></div>,
  },
];

/* ──────────────────────────────────────────────
   Main page component
   ────────────────────────────────────────────── */

const CoolFeatures: React.FC = () => {
  const webFeatures = features.filter((f) => !f.appOnly);
  const appOnlyFeatures = features.filter((f) => f.appOnly);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-neutral-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/assets/logo-mark.png" alt="taskDojo" className="h-8 w-8 rounded-lg" />
            <span className="text-lg font-bold tracking-tight">taskDojo</span>
          </a>
          <a
            href="/"
            className="text-sm text-neutral-400 hover:text-white transition-colors"
          >
            Sign in
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.12) 0%, transparent 60%)',
          }}
        />
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-16 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Everything you need to{' '}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              stay organised
            </span>
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            taskDojo combines task management, AI assistance, habit tracking, and collaboration — all in one beautiful app.
          </p>
        </div>
      </section>

      {/* Web + iOS features */}
      <section className="max-w-6xl mx-auto px-6">
        {webFeatures.map((feature, i) => {
          const reversed = i % 2 === 1;
          return (
            <div
              key={feature.title}
              className={`flex flex-col ${reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-16 py-16 border-t border-neutral-800/50`}
            >
              {/* Visual */}
              <div className="w-full lg:w-1/2 max-w-md mx-auto lg:mx-0">
                {feature.preview}
              </div>
              {/* Text */}
              <div className="w-full lg:w-1/2">
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">{feature.title}</h2>
                <p className="text-purple-400 font-medium mb-4">{feature.tagline}</p>
                <p className="text-neutral-300 leading-relaxed mb-4">{feature.description}</p>
                <div className="rounded-xl bg-neutral-900 border border-neutral-800 p-4">
                  <p className="text-[11px] text-purple-400 uppercase tracking-wider font-medium mb-1">Use case</p>
                  <p className="text-sm text-neutral-400 leading-relaxed">{feature.useCase}</p>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* App-only features */}
      <section className="border-t border-neutral-800/50">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] uppercase tracking-wider font-medium text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full mb-4">
              App only — coming soon to web
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Even more on iOS</h2>
            <p className="text-neutral-400 max-w-xl mx-auto">
              These features are available in the taskDojo iOS app today, with web versions on the way.
            </p>
          </div>

          {appOnlyFeatures.map((feature, i) => {
            const reversed = i % 2 === 1;
            return (
              <div
                key={feature.title}
                className={`flex flex-col ${reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-16 py-12 ${i > 0 ? 'border-t border-neutral-800/30' : ''}`}
              >
                <div className="w-full lg:w-1/2 max-w-md mx-auto lg:mx-0 relative">
                  {feature.preview}
                  <div className="absolute top-3 right-3 text-[9px] uppercase tracking-wider font-medium text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/20">
                    iOS app
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">{feature.title}</h2>
                  <p className="text-purple-400 font-medium mb-4">{feature.tagline}</p>
                  <p className="text-neutral-300 leading-relaxed mb-4">{feature.description}</p>
                  <div className="rounded-xl bg-neutral-900 border border-neutral-800 p-4">
                    <p className="text-[11px] text-purple-400 uppercase tracking-wider font-medium mb-1">Use case</p>
                    <p className="text-sm text-neutral-400 leading-relaxed">{feature.useCase}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-neutral-800/50">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl font-bold mb-3">Ready to get started?</h2>
          <p className="text-neutral-400 mb-8">
            Download taskDojo on iOS or sign in to the web portal.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/"
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-xl text-black bg-white hover:bg-gray-200 transition-all duration-200"
            >
              Sign in to web portal
            </a>
            <a
              href="https://apps.apple.com/gb/app/taskdojo-ai-life-companion/id6753764927"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-xl text-white bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 transition-all duration-200"
            >
              Download on iOS
            </a>
          </div>
          <p className="text-neutral-600 text-xs mt-4">Android coming soon.</p>
        </div>
      </section>
    </div>
  );
};

export default CoolFeatures;
